import { omitBy, isNull } from 'lodash'
import { makeAutoObservable } from 'mobx'

export type IFilterPayloadValue = number | string | (string | number)[]

export type IFilterPayload<TCurrentValue = IFilterPayloadValue> = {
  key: string
  type: string
  operator?: string | null
  to?: string | null
  from?: string | null
  value?: TCurrentValue | null
  is_empty?: number | null
}

export class FilterPayload<TCurrentValue> {
  private _to: IFilterPayload<TCurrentValue>['to'] = null
  private _from: IFilterPayload<TCurrentValue>['from'] = null
  private _value: IFilterPayload<TCurrentValue>['value'] = null

  constructor(payload: Partial<IFilterPayload<TCurrentValue>> = {}) {
    this._to = payload.to ?? null
    this._from = payload.from ?? null
    this._value = payload.value ?? null

    makeAutoObservable(this)
  }

  get to() {
    return this._to
  }

  get from() {
    return this._from
  }

  get value() {
    return this._value
  }

  get isEmpty() {
    return (
      this._to === null &&
      this._from === null &&
      (Array.isArray(this._value) ? !this._value.length : this._value === null)
    )
  }

  get payload() {
    return omitBy(
      {
        to: this._to,
        from: this._from,
        value: this._value,
      },
      isNull
    )
  }

  setValue = (value: IFilterPayload<TCurrentValue>['value']) => {
    this._value = value
  }

  clear() {
    this._to = null
    this._from = null
    this._value = null
  }

  update(payload: Partial<IFilterPayload<TCurrentValue>>) {
    this._to = payload.to ?? this._to
    this._from = payload.from ?? this._from
    this._value = payload.value ?? this._value
  }

  updateHard(payload: Partial<IFilterPayload<TCurrentValue>>) {
    if (payload.hasOwnProperty('to')) this._to = payload.to ?? null
    if (payload.hasOwnProperty('from')) this._from = payload.from ?? null
    if (payload.hasOwnProperty('value')) this._value = payload.value ?? null
  }

  populateJson(data: Record<string, unknown>) {
    this._to = (data.to as IFilterPayload<TCurrentValue>['to']) ?? null
    this._from = (data.from as IFilterPayload<TCurrentValue>['from']) ?? null
    this._value = (data.value as IFilterPayload<TCurrentValue>['value']) ?? null
  }
}
